import React, { useState } from 'react';
import { InternalApiEntityGroup, InternalApiEntityType, InternalApiScenarioLanguage } from '../../../api-internal';
import EditorLayout from '../../../common/editor/EditorLayout';
import {
  SalvButton,
  SalvModal,
  SalvRadioToggleButton,
  SalvRadioToggleGroup,
  SalvTextField,
  Size,
} from '@salvapp/ui-components';
import { Formik, FormikErrors, FormikHelpers, FormikProps } from 'formik';
import * as Yup from 'yup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUp, faCircleXmark, faUser } from '@fortawesome/pro-solid-svg-icons';
import { parseErrorMessage } from '../../../common/errors/helpers';
import { useQuerySegments, useUpdateSegment } from './segmentActions';
import { toSnakeCase } from '../../../common/helper';

const newValues = (type: InternalApiEntityType): SegmentFormValues => {
  return {
    id: undefined,
    name: '',
    code: '',
    rule: '',
    entityType: type,
  };
};

export interface SegmentFormValues {
  id: number | undefined;
  name: string;
  code: string;
  rule: string;
  entityType: InternalApiEntityType;
}

interface Props {
  defaultType: InternalApiEntityType;
  onClose: () => void;
}

const SegmentEditor = ({ defaultType, onClose }: Props) => {
  const [isNameOrCodeFirstEditEnded, setFirstNameOrCodeEditEnded] = useState(false);
  const { data: segments } = useQuerySegments();
  const { mutate: updateSegment } = useUpdateSegment();

  const closeForm = (resetForm: FormikHelpers<SegmentFormValues>['resetForm']) => {
    onClose();
    resetForm();
  };

  const editorParams = ({
    setFieldValue,
    values,
    errors,
    isSubmitting,
  }: {
    setFieldValue: FormikHelpers<SegmentFormValues>['setFieldValue'];
    values: SegmentFormValues;
    errors: FormikErrors<SegmentFormValues>;
    isSubmitting: boolean;
  }) => {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <div>
          <SalvTextField
            label='Name'
            size={Size.SMALL}
            className='m-b-4'
            value={values.name}
            onChange={async (value: string) => {
              await setFieldValue('name', value);
              if (!isNameOrCodeFirstEditEnded) {
                await setFieldValue('code', toSnakeCase(value).toUpperCase());
              }
            }}
            onBlur={() => setFirstNameOrCodeEditEnded(true)}
          />
          {errors.name && <div className={'salv-error-message'}>{errors.name}</div>}
        </div>
        <div>
          <SalvTextField
            label='Code'
            size={Size.SMALL}
            className='m-b-4'
            value={values.code}
            onChange={async (value: string) => await setFieldValue('code', toSnakeCase(value).toUpperCase())}
            disabled={isSubmitting}
            onBlur={() => setFirstNameOrCodeEditEnded(true)}
          />
          {errors.code && <div className={'salv-error-message'}>{errors.code}</div>}
        </div>

        <SalvRadioToggleGroup
          label='Type'
          size={Size.SMALL}
          onChange={(value) => setFieldValue('entityType', value as InternalApiEntityType)}
          value={values.entityType}
        >
          <SalvRadioToggleButton icon={faUser} label='Person' value={InternalApiEntityType.PERSON} />
          <SalvRadioToggleButton icon={faArrowUp} label='Transaction' value={InternalApiEntityType.TRANSACTION} />
        </SalvRadioToggleGroup>
      </div>
    );
  };

  const errorFooter = (error: string | undefined) => {
    if (!error) {
      return undefined;
    }
    return (
      <div className='d-flex align-items-center validation-bar'>
        <FontAwesomeIcon icon={faCircleXmark} aria-label='Errors' className='m-r-8' />
        {error}
      </div>
    );
  };

  const submit = async (values: SegmentFormValues, formikHelpers: FormikHelpers<SegmentFormValues>) => {
    try {
      const segment: InternalApiEntityGroup = values;
      segment.name = segment.name.trim();
      updateSegment(segment);
      closeForm(formikHelpers.resetForm);
    } catch (e) {
      await showError(e, formikHelpers);
    }
  };

  const showError = async (e: unknown, formikHelpers: FormikHelpers<SegmentFormValues>) => {
    formikHelpers.setSubmitting(false);
    const message = await parseErrorMessage(e);
    formikHelpers.setFieldError('value', message);
  };

  const validationSchema = Yup.object()
    .shape({
      name: Yup.string().required('Please provide a name'),
      code: Yup.string()
        .matches(/^[A-Z0-9_]+$/, {
          message: 'Code must contain capital letters, numbers or underscores',
        })
        .required('Please provide a code'),
      entityType: Yup.string().required(''),
      rule: Yup.string().required('The rule must not be empty'),
    })
    .test({
      test: (newSegment) => {
        if (segments?.some((segment) => segment.code === newSegment.code)) {
          return new Yup.ValidationError('A Segment with this code already exists', null, 'code');
        }

        return true;
      },
    });

  return (
    <Formik
      enableReinitialize={true}
      initialValues={newValues(defaultType)}
      onSubmit={submit}
      validationSchema={validationSchema}
    >
      {(formikHelpers: FormikProps<SegmentFormValues>) => {
        const { submitForm, values, errors, isValid, isSubmitting, setFieldValue, dirty, resetForm } = formikHelpers;
        return (
          <SalvModal show={true} onHide={() => closeForm(resetForm)} className='segment-modal'>
            <SalvModal.Header>New Segment</SalvModal.Header>
            <SalvModal.Body>
              <EditorLayout
                disabled={isSubmitting}
                editorType={InternalApiScenarioLanguage.JMESPATH}
                editorValue={values.rule}
                editorOnChange={(newValue) => setFieldValue('rule', newValue)}
                parameters={editorParams({ setFieldValue, values, errors, isSubmitting })}
                footer={errorFooter(errors.rule)}
                showLanguageDropdown={true}
              />
            </SalvModal.Body>
            <SalvModal.Footer>
              <SalvButton
                variant='muted'
                size={Size.LARGE}
                label='Close'
                disabled={isSubmitting}
                onClick={() => closeForm(resetForm)}
              />
              <SalvButton
                type='submit'
                variant='primary'
                size={Size.LARGE}
                label='Save'
                disabled={isSubmitting || !isValid || !dirty}
                onClick={submitForm}
              />
            </SalvModal.Footer>
          </SalvModal>
        );
      }}
    </Formik>
  );
};

export default SegmentEditor;
